class ProductFormModal extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.boundKeyHandler = this.handleKeyDown.bind(this);
    this.boundSubmitHandler = this.handleSubmit.bind(this);
    this.productTags = [];
    this.productVariants = [];
    this.currentItemKey = null;
    this.savedQuestions = {}; // Store question values when switching variants
    this.init();
  }

  async handleSubmit(e) {
    e.preventDefault();

    const productType = this.getProductType();
    const nameInput = this.shadowRoot.getElementById('customerName');
    const dobInput = this.shadowRoot.getElementById('customerDOB');
    const targetNameInput = this.shadowRoot.getElementById('targetName');
    const targetDobInput = this.shadowRoot.getElementById('targetDOB');
    const detailsInput = this.shadowRoot.getElementById('additionalDetails');

    // Reset all error messages and invalid classes
    this.shadowRoot.querySelectorAll('.error-message.show').forEach(error => error.classList.remove('show'));
    this.shadowRoot.querySelectorAll('.invalid').forEach(field => field.classList.remove('invalid'));

    let isValid = true;

    // Validate name
    if (!nameInput.value.trim() || nameInput.value.length > 100) {
      this.shadowRoot.getElementById('nameError').classList.add('show');
      nameInput.classList.add('invalid');
      nameInput.focus();
      isValid = false;
    }

    // Specific validation for custom_spell
    if (productType === 'custom_spell') {
      if (!detailsInput.value.trim()) {
        const errorElement = this.shadowRoot.getElementById('detailsError');
        errorElement.classList.add('show');
        detailsInput.classList.add('invalid');
        if (isValid) detailsInput.focus();
        isValid = false;
      }
    }

    // Specific validation for cbtm_spell
    if (productType === 'cbtm_spell') {
      if (!targetNameInput.value.trim()) {
        const errorElement = this.shadowRoot.getElementById('targetNameError');
        errorElement.classList.add('show');
        targetNameInput.classList.add('invalid');
        if (isValid) targetNameInput.focus();
        isValid = false;
      }
    }

    // Validate DOB if entered
    if (dobInput.value && !this.validateDate(dobInput.value)) {
      this.shadowRoot.getElementById('dobError').classList.add('show');
      if (isValid) dobInput.focus();
      isValid = false;
    }

    // Validate target DOB if entered
    if (targetDobInput.value && !this.validateDate(targetDobInput.value)) {
      targetDobInput.parentNode.querySelector('.error-message').classList.add('show');
      if (isValid) targetDobInput.focus();
      isValid = false;
    }

    // Validate tarot questions - get current variant's question count
    const currentVariantId = document.querySelector('.product-variant-id')?.value;
    const currentProductType = this.getProductType(currentVariantId);
    if (typeof currentProductType === 'object' && currentProductType.type === 'tarot') {
      const firstQuestion = this.shadowRoot.getElementById('question1');
      if (!firstQuestion.value.trim() || firstQuestion.value.length < 10 || firstQuestion.value.length > 250) {
        const errorElement = this.shadowRoot.getElementById('questionError1');
        errorElement.classList.add('show');
        if (isValid) firstQuestion.focus();
        isValid = false;
      }
      for (let i = 2; i <= currentProductType.questionCount; i++) {
        const questionInput = this.shadowRoot.getElementById(`question${i}`);
        if (
          questionInput.value.trim() &&
          (questionInput.value.length < 10 || questionInput.value.length > 250)
        ) {
          const errorElement = this.shadowRoot.getElementById(`questionError${i}`);
          errorElement.classList.add('show');
          if (isValid) questionInput.focus();
          isValid = false;
        }
      }
    }

    if (!isValid) return;

    try {
      this.submitButton.classList.add('loading');
      this.submitButton.disabled = true;

      const formData = {
        'properties[Customer Name]': this.sanitizeInput(nameInput.value),
        'properties[Date of Birth]': this.sanitizeInput(dobInput.value),
        'properties[Person of Interest]': this.sanitizeInput(targetNameInput.value),
        'properties[Person DOB]': this.sanitizeInput(targetDobInput.value)
      };

      if (detailsInput) {
        formData['properties[Additional Details]'] = this.sanitizeInput(detailsInput.value);
      }

      // Get current variant's question count for submission
      const currentVariantId = document.querySelector('.product-variant-id')?.value;
      const currentProductType = this.getProductType(currentVariantId);
      if (typeof currentProductType === 'object' && currentProductType.type === 'tarot') {
        for (let i = 1; i <= currentProductType.questionCount; i++) {
          const questionInput = this.shadowRoot.getElementById(`question${i}`);
          formData[`properties[Question ${i}]`] = this.sanitizeInput(questionInput.value);
        }
      }

      const shopifyRoot = window.Shopify?.routes?.root || '/';

      if (this.currentItemKey) {
        const response = await fetch(`${shopifyRoot}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            id: this.currentItemKey,
            properties: formData
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Failed to update cart');
        }

        const cartData = await response.json();
        this.hideModal();
        this.customForm.reset();
        this.currentItemKey = null;
        this.savedQuestions = {}; // Clear saved questions on successful submit
        
        // Check if VIP was added and decrement stock
        this.handleVipStockUpdate();

        const cartSection = document.querySelector('cart-items');
        if (cartSection) {
          const sectionId = cartSection.querySelector('[data-id]')?.getAttribute('data-id');
          if (sectionId) {
            const url = `${shopifyRoot}?section_id=${sectionId}`;
            const sectionsResponse = await fetch(url);
            const html = await sectionsResponse.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const newCartItems = doc.querySelector('.js-contents');
            if (newCartItems) {
              const currentCartItems = document.querySelector('.js-contents');
              if (currentCartItems) {
                currentCartItems.innerHTML = newCartItems.innerHTML;
              }
            }
          }
        }

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart: cartData }
        }));
      } else {
        if (!this.originalForm) {
          throw new Error('Original cart form not found');
        }
        const cartFormData = new FormData(this.originalForm);
        for (let [key, value] of Object.entries(formData)) {
          cartFormData.append(key, value);
        }

        const response = await fetch(`${shopifyRoot}cart/add.js`, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: cartFormData
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Failed to add to cart');
        }

        this.hideModal();
        this.customForm.reset();
        this.currentItemKey = null;
        this.savedQuestions = {}; // Clear saved questions on successful submit
        
        // Check if VIP was added and decrement stock
        this.handleVipStockUpdate();

        if (typeof window.refreshCart === 'function') {
          window.refreshCart();
        } else {
          // Use location.href to preserve URL parameters (including ?view=ads)
          window.location.href = window.location.href;
        }
      }
    } catch (error) {
      console.error('Error:', error);
      const existingError = this.submitButton.previousElementSibling;
      if (existingError?.classList.contains('error-message')) {
        existingError.remove();
      }
      const errorDiv = document.createElement('div');
      errorDiv.className = 'error-message show';
      errorDiv.textContent = error.message || 'Failed to add item to cart. Please try again.';
      this.submitButton.parentNode.insertBefore(errorDiv, this.submitButton);
    } finally {
      this.submitButton.classList.remove('loading');
      this.submitButton.disabled = false;
    }
  }

  // Supports both original tags (spell_casting, custom_spell, cbtm_spell) 
  // and WWWBF-safe alternatives (ritual_service, custom_ritual, cbtm_ritual)
  getProductType(variantId = null) {
    const tags = this.productTags;
    if (tags.includes('spell_casting') || tags.includes('ritual_service')) {
      if (tags.includes('custom_spell') || tags.includes('custom_ritual')) {
        return 'custom_spell';
      } else if (tags.includes('cbtm_spell') || tags.includes('cbtm_ritual')) {
        return 'cbtm_spell';
      }
      return 'spell';
    } else if (tags.includes('tarot_reading')) {
      // Get question count based on variant if provided
      const questionCount = this.getQuestionCountForVariant(variantId);
      return { type: 'tarot', questionCount: questionCount };
    }
    return 'regular';
  }

  /**
   * Get question count for a specific variant
   * Standard = 1 question, Premium/VIP = 3 questions
   * Falls back to product tag if variant doesn't match
   */
  getQuestionCountForVariant(variantId = null) {
    if (variantId) {
      const variant = this.productVariants.find(v => v.id == variantId);
      if (variant) {
        const variantTitle = (variant.title || variant.option1 || '').toLowerCase().trim();
        if (variantTitle === 'standard') {
          return 1;
        } else if (variantTitle === 'premium' || variantTitle === 'vip') {
          return 3;
        }
      }
    }
    
    // Fallback to product tag
    const questionTag = this.productTags.find(tag => tag.startsWith('questions_'));
    return questionTag ? parseInt(questionTag.split('_')[1]) : 1;
  }

  createTarotFields(questionCount, savedValues = {}) {
    // Validate questionCount
    if (!questionCount || questionCount < 1 || questionCount > 3) {
      questionCount = 1; // Default to 1 if invalid
    }
    
    let questionFields = '';
    for (let i = 1; i <= questionCount; i++) {
      const savedValue = savedValues[i] || '';
      // Escape HTML to prevent XSS
      const escapedValue = this.escapeHtml(savedValue);
      questionFields += `
        <div class="form-row">
          <label for="question${i}">Question ${i}</label>
          <textarea 
            id="question${i}" 
            name="question${i}"
            required
            minlength="10"
            maxlength="250"
            placeholder="Enter your question..."
          >${escapedValue}</textarea>
          <div class="error-message" id="questionError${i}">Question must be between 10 and 250 characters</div>
        </div>
      `;
    }
    return questionFields;
  }

  /**
   * Escape HTML to prevent XSS attacks
   */
  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Save current question values before removing fields
   * Preserves ALL existing question values, even if they're about to be hidden
   */
  saveCurrentQuestions() {
    // Initialize savedQuestions if it doesn't exist
    if (!this.savedQuestions) {
      this.savedQuestions = {};
    }
    
    // Save all questions that currently exist in the DOM (up to 3)
    // This preserves values even when switching to a variant with fewer questions
    for (let i = 1; i <= 3; i++) {
      const questionInput = this.shadowRoot.getElementById(`question${i}`);
      if (questionInput) {
        // Update with current value if it exists
        const currentValue = questionInput.value || '';
        if (currentValue) {
          this.savedQuestions[i] = currentValue;
        }
      }
      // If question doesn't exist in DOM, keep the previously saved value (don't overwrite)
    }
    
    // Debug log removed for production
  }

  /**
   * Update tarot question fields when variant changes
   */
  updateTarotFieldsForVariant(variantId) {
    const productType = this.getProductType(variantId);
    if (typeof productType !== 'object' || productType.type !== 'tarot') {
      return; // Not a tarot product
    }

    // Save current values before updating
    this.saveCurrentQuestions();

    // Find the container where tarot fields are
    const form = this.shadowRoot.getElementById('CustomProductForm');
    if (!form) return;

    // Find all existing question field containers and remove them
    // Use more specific selector to avoid removing other form-rows
    for (let i = 1; i <= 3; i++) {
      const questionRow = form.querySelector(`#question${i}`)?.closest('.form-row');
      if (questionRow) {
        questionRow.remove();
      }
    }

    // Create new fields with saved values
    const newFieldsHtml = this.createTarotFields(productType.questionCount, this.savedQuestions);
    
    // Find the "Additional Details" field or submit button to insert after
    const additionalDetailsRow = form.querySelector('textarea[id="additionalDetails"]')?.closest('.form-row');
    const submitButton = form.querySelector('.submit-button');
    const insertBefore = submitButton;
    
    if (insertBefore) {
      // Create a temporary container to parse HTML (use document.createElement for shadow DOM)
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = newFieldsHtml;
      
      // Move all children from temp container to form, before submit button
      while (tempContainer.firstChild) {
        insertBefore.parentNode.insertBefore(tempContainer.firstChild, insertBefore);
      }
    }
  }

  init() {
    try {
      const tagsAttr = this.getAttribute('data-product-tags');
      this.productTags = tagsAttr ? JSON.parse(tagsAttr) : [];
    } catch (e) {
      console.error('Error parsing product tags:', e);
      this.productTags = [];
    }

    try {
      const variantsAttr = this.getAttribute('data-product-variants');
      this.productVariants = variantsAttr ? JSON.parse(variantsAttr) : [];
    } catch (e) {
      console.error('Error parsing product variants:', e);
      this.productVariants = [];
    }

    const productTitle = this.getAttribute('data-product-title') || '';
    const productPrice = this.getAttribute('data-product-price') || '';
    const productUrl   = this.getAttribute('data-product-url')   || '#';
    const productImage = this.getAttribute('data-product-image') || '';

    this.attachShadow({ mode: 'open' });

    const style = document.createElement('style');
    style.textContent = `
      .product-form-modal {
        display: flex;
        opacity: 0;
        visibility: hidden;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 999999;
        background: rgba(0, 0, 0, 0.7);
        transition: opacity 0.4s ease-out, visibility 0.4s ease-out;
        overscroll-behavior: contain;
      }
    
      .product-form-modal.active {
        opacity: 1;
        visibility: visible;
      }
    
      .product-form-modal .price__regular {
        display: none !important;
      }
    
      .modal-product-info {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 12px;
        margin: 12px 0px 4px;
      }
    
      .product-info-left {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
      }
      .product-modal-image {
        max-width: 180px;
        height: auto;
        border-radius: 4px;
        display: block;
      }
    
      .product-info-right {
        display: flex;
        flex-direction: column;
        gap: 0px;
        color: white;
      }
      .product-modal-title {
        font-size: 18px;
        font-family: 'cinzeldecorative';
        font-weight: 500;
        margin-top: 25px;
        margin-bottom: 0;
      }
    
      .product-price {
        font-size: 16px;
        font-weight: 400;
        color: #fff;
      }
    
      .modal-variant-selectors {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        padding: 0;
      }
    
      .guarantee-banner {    
        display: flex;
        justify-content: space-between;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
        color: #ffffff;
        font-size: 1rem;
        font-weight: 500;
        white-space: nowrap;  
      }
    
      .banner-inner {
        display: flex;
        flex-direction: row;
        gap: 1rem;
        width: 100%;
      }
    
      .guarantee-item {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
    
      .view-full-details {
        text-decoration: none;
        color: #adbf55;
      }
    
      .price {
        display: flex;
        flex-direction: column;
        gap: 0px;
      }
    
      .badge-container {
        order: -2;
      }
    
      .percentage-badge {
        margin-left: 5%;
      }
    
      .price-item--regular {
        order: -1;
        font-size: 12px;
        text-decoration: line-through;
        color: #999;
      }
    
      .price-item--sale {
        font-size: 22px;
        color: #fff;
        margin-left: 0;
      }
    
      .price__container {
        display: flex;
        flex-direction: column;
        gap: 0px;
      }
    
      .price__sale {
        flex-direction: column;
        gap: 0px;
      }
    
      .modal-variant-selectors {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px;
        padding: 5px 0;
        width: 100%;
      }
    
      .variant-wrapper {
        flex: 0 1 auto;
        display: flex;
        justify-content: center;
      }
    
      .modal-variant-selectors label {
        border: 1px solid #adbf555e;
        background-color: black;
        color: white;
        border-radius: 25px;
        display: inline-block;
        margin: 0;
        padding: 2px 5px;
        font-size: 1.2rem;
        letter-spacing: .1rem;
        line-height: 1;
        text-align: center;
        transition: all 0.2s ease;
        cursor: pointer;
        position: relative;
        min-width: 120px;
        text-align: center;
      }
    
      .modal-variant-selectors input[type="radio"] {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
      }
    
      .modal-variant-selectors input[type="radio"]:checked + label {
        background-color: #adbf553d;
        color: #ffffff;
      }
      
      .modal-variant-selectors label.vip-unavailable {
        opacity: 0.5;
        cursor: not-allowed;
        background-color: rgba(255, 71, 87, 0.3);
        border-color: #adbf55;
        color: #ffffff;
      }
      
      .vip-unavailable-message {
        background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
        border: 2px solid #ff4757;
        border-radius: 8px;
        padding: 12px;
        margin: 12px 0;
        animation: slideIn 0.3s ease-out;
      }
      
      .vip-message-content {
        display: flex;
        align-items: center;
        gap: 8px;
        color: white;
        font-weight: 500;
      }
      
      .vip-message-icon {
        font-size: 18px;
      }
      
      .vip-message-text {
        font-size: 14px;
      }
      
    
      .guarantee-banner {
        width: 100%;
        overflow: hidden;
        padding: .5rem 0rem 0rem;
        border-radius: 4px;
        display: flex;
        justify-content: center;
      }
    
      .banner-inner {
        display: flex;
        justify-content: space-between;
        flex-direction: row;
        width: 100%;
        padding: 0rem 1rem;
      }
    
      .guarantee-item {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: #ffffff;
        font-size: 1rem;
        font-weight: 500;
        white-space: nowrap;
      }
    
      .check-icon {
        color: #adbf55;
        font-weight: bold;
      }
    
      .product-form-modal__content {
        background: black;
        width: 95%;
        max-width: 500px;
        margin: 0 auto;
        padding: 6px 5px calc(12px + env(safe-area-inset-bottom));
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
        border-bottom-left-radius: 16px;
        border-bottom-right-radius: 16px;
        border: 1px solid #adbf555e;
        border-top-width: 1px;
        border-right-width: 1px;
        border-bottom-width: 1px;       
        border-left-width: 1px;         
        transform: translateY(100%);
        transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        position: fixed;
        left: 0;
        right: 0;
        bottom: env(safe-area-inset-bottom);
        top: auto;
        max-height: min(90dvh, 720px);
        max-height: min(90svh, 720px);
        max-height: calc(90vh - 80px);
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
      }
    
      .product-form-modal.active .product-form-modal__content {
        transform: translateY(0);
      }
    
      .form-header {
        text-align: center;
      }
    
      .product-form-modal .form-title {
        font-family: 'cinzeldecorative';
        font-size: 20px;
        font-weight: 500;
        margin: 1px;
        color: #adbf55;
      }
    
      .form-subtitle {
        font-size: 16px;
        color: white;
        margin: 0px 0px 4px;
        line-height: 1.2;
      }
    
      .form-row {
        margin-bottom: 0px;
      }
    
      .form-row.inline {
        display: flex;
        gap: 12px;
        width: 100%;
        box-sizing: border-box;
        flex-wrap: wrap;
        margin: 0px 0px 6px;
      }
    
      .form-row.inline .name-field {
        flex: 1;
        min-width: 200px;
      }
    
      .form-row.inline .dob-field {
        flex: 0 1;
        min-width: 120px;
      }
    
      .form-row label {
        display: block;
        font-weight: 500;
        color: white;
        font-family: Arial, sans-serif;
      }
    
      .form-row input {
        width: 95%;
        padding: 5px;
        border: 2px solid #E5E7EB;
        border-radius: 8px;
        font-size: 16px;
        transition: all 0.3s ease;
        box-sizing: border-box;
        background: white;
        color: black;
      }
    
      .form-row input.invalid {
        border-color: #ff4444;
        background-color: #fff0f0;
      }
    
      .form-row input:focus {
        outline: none;
        border-color: #666;
        box-shadow: 0 0 0 3px rgba(255,255,255,0.1);
      }
    
      .form-row textarea {
        width: 100%;
        padding: 1px 4px;
        border: 2px solid #E5E7EB;
        border-radius: 8px;
        font-size: 16px;
        min-height: 60px;
        resize: vertical;
        transition: border-color 0.3s ease;
        box-sizing: border-box;
        background: white;
        color: black;
      }
    
      .form-row textarea:focus {
        outline: none;
        border-color: #666;
        box-shadow: 0 0 0 3px rgba(255,255,255,0.1);
      }
    
      .error-message {
        color: #ff4444;
        font-size: 14px;
        margin-top: 4px;
        display: none;
      }
    
      .error-message.show {
        display: block;
      }
    
      .close-button {
        position: fixed;
        background: none;
        border: none;
        padding: 8px;
        cursor: pointer;
        font-size: 20px;
        color: white;
        z-index: 1;
      }
    
      .close-button:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba(255,255,255,0.1);
        border-radius: 4px;
      }
    
      /* modal footer removed to keep CTA inline */

      .submit-button {
        width: 92%;
        text-align: center;
        margin: 10px auto 0px;
        display: block;
        padding: 10px;
        color: white;
        cursor: pointer;
        font-size: 16px;
        font-weight: 500;
        transition: all 0.3s ease;
        
        /* Your custom fading black background */
        background: linear-gradient(90deg, 
          rgba(0, 0, 0, 0.1),
          rgba(0, 0, 0, 0.6), 
          rgba(0, 0, 0, 1), 
          rgb(0, 0, 0), 
          rgba(0, 0, 0, 1), 
          rgba(0, 0, 0, 0.6), 
          rgba(0, 0, 0, 0.1)
        );
        
        /* Gold gradient borders - TOP and BOTTOM only */
        border-left: none;
        border-right: none;
        border-top: 1px solid transparent;
        border-bottom: 1px solid transparent;
        border-image: linear-gradient(90deg, 
          rgba(184, 148, 28, 0.2),
          rgba(212, 175, 55, 0.7),
          rgba(230, 201, 101, 1),
          rgba(212, 175, 55, 0.7),
          rgba(184, 148, 28, 0.2)
        ) 1;
        
        /* Sharp edges (no rounded corners) */
        border-radius: 0px;
        
        /* Subtle gold glow - LEFT and RIGHT sides only */
        box-shadow: 
          -4px 0 6px rgba(212, 175, 55, 0.15),
          4px 0 6px rgba(212, 175, 55, 0.15);
      }
    
      .submit-button:hover {
        /* Brighter gold borders on hover */
        background: linear-gradient(90deg, 
          rgba(0, 0, 0, 0.1),
          rgba(0, 0, 0, 0.6), 
          rgba(0, 0, 0, 1), 
          rgb(0, 0, 0), 
          rgba(0, 0, 0, 1), 
          rgba(0, 0, 0, 0.6), 
          rgba(0, 0, 0, 0.1)
        );
        
        border-image: linear-gradient(90deg, 
          rgba(184, 148, 28, 0.5),
          rgba(212, 175, 55, 1),
          rgba(230, 201, 101, 1),
          rgba(212, 175, 55, 1),
          rgba(184, 148, 28, 0.5)
        ) 1;
        
        /* Intensified side glow on hover */
        box-shadow: 
          -6px 0 8px rgba(212, 175, 55, 0.25),
          6px 0 8px rgba(212, 175, 55, 0.25);
        
        /* Subtle lift */
        transform: translateY(-2px);
      }
    
      .submit-button:disabled {
        opacity: 0.7;
        cursor: not-allowed;
        background: #999;
        border-image: none;
        box-shadow: none;
      }
    
      .submit-button.loading .button-content {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
      }
    
      .spinner {
        display: none;
        width: 20px;
        height: 20px;
        border: 2px solid #fff;
        border-radius: 50%;
        border-top-color: transparent;
        animation: spin 0.8s linear infinite;
      }
    
      .submit-button.loading .spinner {
        display: block;
      }
    
      .price__badge-sold-out,
      .price__badge-sale,
      .price-item--compare,
      .price-item--savings {
        display: none;
      }
    
      @keyframes spin {
        100% { transform: rotate(360deg); }
      }
    
      .visually-hidden {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        border: 0;
      }
    
      .product-form-modal .price__regular {
        display: none !important;
      }
    
      .unit-price [aria-hidden="true"] {
        display: none;
      }
    
      .view-full-details {
        text-decoration: none;
        color: #adbf55;
        display: flex;
        align-items: center;
        gap: 5px;
      }
    
      .view-full-details::after {
        content: "➝";
        display: inline-block;
      }
    
      .form-row input.invalid,
      .form-row textarea.invalid {
        border-color: #ff4444;
        border-width: 2px;
        background-color: #fff0f0;
      }
    
      .form-row input:focus.invalid,
      .form-row textarea:focus.invalid {
        box-shadow: 0 0 0 3px rgba(255,68,68,0.1);
      }
    
      @media (max-width: 375px) {
        .product-form-modal__content {
          max-height: 100vh;
        }
      }

      @media (max-width: 767px) {
        .product-form-modal__content {
          max-height: 80vh ;
        }
      }   

      @media (max-width: 767px) {
        .product-modal-image {
          max-width: 180px;
          
        }
      }
    `;

    const productInfoHtml = `
      <div class="modal-product-info">
        <div class="product-info-left">
          <img src="${productImage}" alt="${productTitle}" class="product-modal-image">
        </div>
        <div class="product-info-right">
          <h2 class="product-modal-title">${productTitle}</h2>
          <div class="product-price ajax-price-block">Loading price...</div>
          <a class="view-full-details" href="${productUrl}" target="_blank">View full details</a>
        </div>
      </div>
      <div class="modal-variant-selectors"></div>
      <div class="guarantee-banner">
        <div class="banner-inner">
          <div class="guarantee-item"><span class="check-icon">✓</span>100% Money Back Guarantee</div>
          <div class="guarantee-item"><span class="check-icon">✓</span>96% Customer Satisfaction Rate</div>
        </div>
      </div>
    `;

    // Get initial variant to determine question count for tarot
    const currentIdInput = document.querySelector('form[data-type="add-to-cart-form"] input[name="id"], input[name="id"]');
    const initialVariantId = currentIdInput ? String(currentIdInput.value) : null;
    
    const productType = this.getProductType(initialVariantId);
    let formFields = '';

    if (
      productType === 'spell' ||
      productType === 'custom_spell' ||
      productType === 'cbtm_spell' ||
      productType === 'regular'
    ) {
      formFields = `
        <div class="form-row inline">
          <div class="name-field">
            <label for="customerName">Your Name</label>
            <input 
              type="text" 
              id="customerName" 
              name="customerName"
              required
              maxlength="100"
              autocomplete="name"
              placeholder="Enter your full name"
            >
            <div class="error-message" id="nameError">Please enter your name</div>
          </div>
          <div class="dob-field">
            <label for="customerDOB">Date of Birth</label>
            <input 
              type="text" 
              id="customerDOB" 
              name="customerDOB"
              placeholder="DD/MM/YYYY"
            >
            <div class="error-message" id="dobError">Invalid date format</div>
          </div>
        </div>
        <div class="form-row inline">
          <div class="name-field">
            <label for="targetName">Person of Interest</label>
            <input 
              type="text" 
              id="targetName" 
              name="targetName"
              maxlength="100"
              placeholder="Enter their full name"
              ${productType === 'cbtm_spell' ? 'required' : ''}
            >
            <div class="error-message" id="targetNameError">${
              productType === 'cbtm_spell'
                ? 'Please provide the name of your person of interest'
                : ''
            }</div>
          </div>
          <div class="dob-field">
            <label for="targetDOB">Date of Birth</label>
            <input 
              type="text" 
              id="targetDOB" 
              name="targetDOB"
              placeholder="DD/MM/YYYY"
            >
          </div>
        </div>
        <div class="form-row">
          <label for="additionalDetails">Additional Details</label>
          <textarea 
            id="additionalDetails" 
            name="additionalDetails"
            maxlength="500"
            placeholder="Any specific details or requests..."
            ${productType === 'custom_spell' ? 'required' : ''}
          ></textarea>
          <div class="error-message" id="detailsError">${
            productType === 'custom_spell'
              ? 'Please provide details for your custom spell request'
              : 'Please provide any additional details'
          }</div>
        </div>
      `;
    } else if (typeof productType === 'object' && productType.type === 'tarot') {
      formFields = `
        <div class="form-row inline">
          <div class="name-field">
            <label for="customerName">Your Name</label>
            <input 
              type="text" 
              id="customerName" 
              name="customerName"
              required
              maxlength="100"
              autocomplete="name"
              placeholder="Enter your full name"
            >
            <div class="error-message" id="nameError">Please enter your name</div>
          </div>
          <div class="dob-field">
            <label for="customerDOB">Date of Birth</label>
            <input 
              type="text" 
              id="customerDOB" 
              name="customerDOB"
              placeholder="DD/MM/YYYY"
            >
            <div class="error-message" id="dobError">Invalid date format</div>
          </div>
        </div>
        <div class="form-row inline">
          <div class="name-field">
            <label for="targetName">Person of Interest</label>
            <input 
              type="text" 
              id="targetName" 
              name="targetName"
              maxlength="100"
              placeholder="Enter their full name"
            >
            <div class="error-message" id="targetNameError">Please enter your person of interest’s name</div>
          </div>
          <div class="dob-field">
            <label for="targetDOB">Date of Birth</label>
            <input 
              type="text" 
              id="targetDOB" 
              name="targetDOB"
              placeholder="DD/MM/YYYY"
            >
            <div class="error-message" id="dobError">Invalid date format</div>
          </div>
        </div>
        <div class="form-row">
          <label for="additionalDetails">Additional Details</label>
          <textarea 
            id="additionalDetails" 
            name="additionalDetails"
            maxlength="500"
            placeholder="Any specific details or requests..."
            ${productType === 'custom_spell' ? 'required' : ''}
          ></textarea>
          <div class="error-message" id="detailsError">${
            productType === 'custom_spell'
              ? 'Please provide details about your custom spell request'
              : 'Please provide any additional details'
          }</div>
        </div>
        ${this.createTarotFields(productType.questionCount)}
      `;
    }

    const modalHtml = `
      <div id="ProductFormModal" class="product-form-modal" role="dialog" aria-modal="true" aria-labelledby="modalTitle">
        <div class="product-form-modal__content">
          <button class="close-button" aria-label="Close" style="top: calc(env(safe-area-inset-top, 0px) + 8px); right: 12px;">✕</button>
          ${productInfoHtml}
          <div class="form-header">
            <h2 id="modalTitle" class="form-title">Complete Your Order</h2>
            <p class="form-subtitle">Please provide the details needed for your custom item</p>
          </div>
          <form id="CustomProductForm" novalidate>
            ${formFields}
            <button type="submit" class="submit-button">
              <span class="button-content">
                <span class="spinner" aria-hidden="true"></span>
                <span class="button-text">Add to Cart</span>
              </span>
            </button>
          </form>
        </div>
      </div>
    `;

    this.shadowRoot.innerHTML = `${style.outerHTML}${modalHtml}`;

    this.modal = this.shadowRoot.getElementById('ProductFormModal');
    this.closeButton = this.shadowRoot.querySelector('.close-button');
    this.customForm = this.shadowRoot.getElementById('CustomProductForm');
    this.submitButton = this.shadowRoot.querySelector('.submit-button');

    // Move host to document.body to ensure fixed positioning uses viewport, not transformed ancestors
    try {
      if (!this._movedToBody) {
        document.body.appendChild(this);
        this._movedToBody = true;
      }
    } catch (e) {
      console.warn('Could not portal product-form-modal to body:', e);
    }

    this.setupOriginalForm();
    this.setupEventListeners();
    this.setupVariantSelectors();
    this.setupVariantChangeListener();
  }

  /**
   * Setup listener for variant changes on the product page
   */
  setupVariantChangeListener() {
    if (typeof subscribe === 'function') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (this.isOpen && event?.data?.variant) {
          this.updateModalForCurrentVariant();
        }
      });
    }

    document.addEventListener('change', (e) => {
      if (this.isOpen && e.target.matches('.variant-row__radio')) {
        setTimeout(() => {
          this.updateModalForCurrentVariant();
        }, 0);
      }
    });
  }

  /**
   * Update modal to reflect current variant selection
   */
  findPreferredVariant(excludeVip = true) {
    if (!this.productVariants.length) return null;

    const availableVariants = this.productVariants.filter((variant) => variant.available);
    const preferenceOrder = excludeVip ? ['premium', 'standard'] : ['vip', 'premium', 'standard'];

    for (const preference of preferenceOrder) {
      const match = availableVariants.find((variant) =>
        (variant.option1 || variant.title || '').toLowerCase().includes(preference)
      );
      if (match) return match;
    }

    return availableVariants[0] || this.productVariants[0] || null;
  }

  updateModalForCurrentVariant() {
    // Get current variant from product page
    const currentIdInput = document.querySelector('form[data-type="add-to-cart-form"] input[name="id"], input[name="id"]');
    let currentVariantId = currentIdInput ? String(currentIdInput.value) : null;
    
    // If no variant ID found, try to get it from the checked variant button
    if (!currentVariantId) {
      const checkedVariant = document.querySelector('input[value="Standard"]:checked, input[value="Premium"]:checked, input[value="VIP"]:checked');
      if (checkedVariant) {
        const variantTitle = checkedVariant.value;
        const variant = this.productVariants.find(v => v.title === variantTitle);
        if (variant) {
          currentVariantId = String(variant.id);
        }
      }
    }
    
    if (!currentVariantId) return;
    
    const vipAvailability = this.checkVipAvailability();
    const isVipUnavailable = vipAvailability.status !== 'open';

    let currentVariant = this.productVariants.find((v) => String(v.id) === currentVariantId);
    if (!currentVariant) {
      currentVariant = this.findPreferredVariant(false);
      currentVariantId = currentVariant ? String(currentVariant.id) : currentVariantId;
    }

    if (currentVariant && isVipUnavailable && currentVariant.title === 'VIP') {
      const fallbackVariant = this.findPreferredVariant(true);
      if (fallbackVariant) {
        currentVariant = fallbackVariant;
        currentVariantId = String(fallbackVariant.id);
      }
    }

    if (!currentVariant) return;
    
    // Update modal variant selection
    const modalInput = this.shadowRoot.querySelector(`#modal_variant_${currentVariant.id}`);
    if (modalInput) {
      modalInput.checked = true;
      this.handleVariantChange(currentVariant.id);
      
      // VIP button is already disabled in setupVariantSelectors - no message needed
    }
  }

  /**
   * Check VIP availability using the same logic as the timer
   */
  checkVipAvailability() {
    // Look for the VIP timer on the product page to get current status
    const vipTimerOpen = document.querySelector('.vip-timer--product-open');
    const vipTimerUnavailable = document.querySelector('.vip-timer--product-unavailable');
    const vipTimerSoldOut = document.querySelector('.vip-timer--product-sold-out');
    
    if (vipTimerOpen) {
      return { status: 'open' };
    } else if (vipTimerSoldOut) {
      return { status: 'sold_out' };
    } else if (vipTimerUnavailable) {
      return { status: 'unavailable' };
    } else {
      // Fallback: assume VIP is available if no timer found
      return { status: 'open' };
    }
  }


  setupVariantSelectors() {
    const variantSelectorsWrapper = this.shadowRoot.querySelector('.modal-variant-selectors');
    if (!variantSelectorsWrapper) return;

    if (this.productVariants.length > 0) {
      // Get the currently selected variant from the product page
      const currentIdInput = document.querySelector('form[data-type="add-to-cart-form"] input[name="id"], input[name="id"]');
      let currentVariantId = currentIdInput ? String(currentIdInput.value) : null;
      
      // If no variant ID found, try to get it from the checked variant button
      if (!currentVariantId) {
        const checkedVariant = document.querySelector('input[value="Standard"]:checked, input[value="Premium"]:checked, input[value="VIP"]:checked');
        if (checkedVariant) {
          const variantTitle = checkedVariant.value;
          const variant = this.productVariants.find(v => v.title === variantTitle);
          if (variant) {
            currentVariantId = String(variant.id);
          }
        }
      }

      // Check VIP availability
      const vipAvailability = this.checkVipAvailability();
      const isVipUnavailable = vipAvailability.status !== 'open';
      const vipStatusText =
        vipAvailability.status === 'sold_out'
          ? 'Sold Out'
          : vipAvailability.status === 'unavailable'
          ? 'Unavailable'
          : 'Unavailable';

      if (isVipUnavailable) {
        const fallbackVariant = this.findPreferredVariant(true);
        if (fallbackVariant) {
          currentVariantId = String(fallbackVariant.id);
        }
      }

      const radiosHtml = this.productVariants.map((variant, idx) => {
        const variantTitleLower = (variant.title || variant.option1 || '').toLowerCase().trim();
        const isVip = variantTitleLower === 'vip';
        const isChecked = currentVariantId ? String(variant.id) === currentVariantId : idx === 0;
        const checkedAttr =
          isVip && isVipUnavailable
            ? ''
            : isChecked
            ? 'checked'
            : '';
        
        return `
          <div class="variant-wrapper">
            <input
              type="radio"
              name="modal_variant_id"
              id="modal_variant_${variant.id}"
              value="${variant.id}"
              ${checkedAttr}
              ${isVip && isVipUnavailable ? 'disabled' : ''}
            >
            <label for="modal_variant_${variant.id}" ${isVip && isVipUnavailable ? 'class="vip-unavailable"' : ''}>
              ${variant.title}
              ${isVip && isVipUnavailable ? ` (${vipStatusText})` : ''}
            </label>
          </div>
        `;
      }).join('');

      variantSelectorsWrapper.innerHTML = radiosHtml;
      
           // VIP button is already disabled - no message needed

      // Ensure modal reflects current selection immediately
      const preselected = variantSelectorsWrapper.querySelector('[name="modal_variant_id"]:checked');
      if (preselected) {
        this.handleVariantChange(preselected.value);
      }

      variantSelectorsWrapper.addEventListener('change', (e) => {
        if (e.target.matches('[name="modal_variant_id"]')) {
          this.handleVariantChange(e.target.value);
        }
      });
    }
  }

  handleVariantChange(newVariantId) {
    const variantIdInput = document.querySelector('.product-variant-id');
    if (variantIdInput) {
      variantIdInput.value = newVariantId;
    }

    const productUrl = this.getAttribute('data-product-url') || '#';
    const snippetUrl = `${productUrl}?section_id=price-block&variant=${newVariantId}`;

    fetch(snippetUrl)
      .then(res => res.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const snippetElement = doc.querySelector('.price-quantity-container');
        const modalPriceBlock = this.shadowRoot.querySelector('.ajax-price-block');
        if (snippetElement && modalPriceBlock) {
          modalPriceBlock.innerHTML = snippetElement.innerHTML;
        }
      })
      .catch(err => console.error('Failed to fetch snippet for variant:', err));

    const selectedVariant = this.productVariants.find(v => v.id == newVariantId);
    if (selectedVariant && selectedVariant.featured_image) {
      const modalImage = this.shadowRoot.querySelector('.product-info-left img');
      if (modalImage) {
        modalImage.src = selectedVariant.featured_image.src;
      }
    }

    // Update tarot question fields if this is a tarot product
    this.updateTarotFieldsForVariant(newVariantId);
  }

  setupOriginalForm() {
    const addToCartButton = document.querySelector('button[name="add"]');
    if (addToCartButton) {
      addToCartButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.showModal();
        return false;
      }, true);
    }

    const cartForm = document.querySelector('form[data-type="add-to-cart-form"]');
    if (cartForm) {
      this.originalForm = cartForm;
      cartForm.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }, true);
    }
  }

  setupEventListeners() {
    this.closeButton.addEventListener('click', () => this.hideModal());
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.hideModal();
      }
    });

    this.customForm.addEventListener('submit', this.boundSubmitHandler);

    this.modal.addEventListener('touchmove', (e) => {
      if (e.target === this.modal) {
        e.preventDefault();
      }
    }, { passive: false });

    const dobInputs = this.shadowRoot.querySelectorAll('input[placeholder="DD/MM/YYYY"]');
    dobInputs.forEach(input => {
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Backspace' || e.key === 'Delete') {
          e.preventDefault();
          let value = input.value.replace(/\//g, '');
          value = value.slice(0, -1);

          if (value.length >= 4) {
            value = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4)}`;
          } else if (value.length >= 2) {
            value = `${value.slice(0, 2)}/${value.slice(2)}`;
          }

          input.value = value;
          validateDateInput(input);
        }
      });

      input.addEventListener('input', (e) => {
        if (e.inputType === 'deleteContentBackward' || e.inputType === 'deleteContentForward') return;

        let value = e.target.value.replace(/\D/g, '');
        if (value.length > 8) value = value.slice(0, 8);

        if (value.length >= 4) {
          value = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4)}`;
        } else if (value.length >= 2) {
          value = `${value.slice(0, 2)}/${value.slice(2)}`;
        }

        e.target.value = value;
        validateDateInput(input);
      });
    });

    function validateDateInput(input) {
      if (input.value.length !== 10) {
        input.classList.remove('invalid');
        return;
      }
      const parts = input.value.split('/');
      if (parts.length !== 3) {
        input.classList.add('invalid');
        return;
      }
      const [p1, p2, p3] = parts.map(Number);
      const year = p3;
      if (isNaN(year) || year < 1900 || year > new Date().getFullYear()) {
        input.classList.add('invalid');
        return;
      }
      const A = p1;
      const B = p2;
      const option1 = A >= 1 && A <= 31 && B >= 1 && B <= 12;
      const option2 = A >= 1 && A <= 12 && B >= 1 && B <= 31;
      if (option1 || option2) {
        input.classList.remove('invalid');
      } else {
        input.classList.add('invalid');
      }
    }
  }

  connectedCallback() {
    document.addEventListener('keydown', this.boundKeyHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.boundKeyHandler);
    this.customForm.removeEventListener('submit', this.boundSubmitHandler);
    this.variantChangeUnsubscriber?.();
  }

  handleKeyDown(e) {
    if (e.key === 'Escape' && this.isOpen) {
      this.hideModal();
    }
  }

  validateDate(dateString) {
    if (!dateString) return false;
    const parts = dateString.split('/');
    if (parts.length !== 3) return false;

    const [p1, p2, p3] = parts.map(Number);
    const year = p3;
    if (isNaN(year) || year < 1900 || year > new Date().getFullYear()) {
      return false;
    }

    const A = p1;
    const B = p2;
    const option1 = A >= 1 && A <= 31 && B >= 1 && B <= 12;
    const option2 = A >= 1 && A <= 12 && B >= 1 && B <= 31;
    return option1 || option2;
  }

  sanitizeInput(input) {
    if (!input) return '';
    return input.toString().replace(/[<>]/g, '').trim();
  }

  async showModal() {
    if (this.isOpen) return;

    const productType = this.getProductType();
    if (productType === 'regular') {
      this.originalForm?.submit();
      return;
    }

    try {
      const productUrl = this.getAttribute('data-product-url') || '#';
      const snippetUrl = `${productUrl}?section_id=price-block`;

      const response = await fetch(snippetUrl);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const snippetElement = doc.querySelector('.price-quantity-container') || doc.body;
      const modalPriceBlock = this.shadowRoot.querySelector('.ajax-price-block');
      if (snippetElement && modalPriceBlock) {
        modalPriceBlock.innerHTML = snippetElement.innerHTML;
      }
    } catch (err) {
      console.error('Failed to fetch price-block snippet:', err);
    }

    this.isOpen = true;
    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Update variant selectors to reflect current product page selection
    this.setupVariantSelectors();
    this.updateModalForCurrentVariant();

    this.previousActiveElement = document.activeElement;
    const contentEl = this.shadowRoot.querySelector('.product-form-modal__content');
    if (contentEl) contentEl.scrollTop = 0;
    const firstInput = this.shadowRoot.querySelector('input');
    setTimeout(() => firstInput?.focus(), 100);

    // Ensure panel always starts just below sticky site header if present
    try {
      const siteHeader = document.querySelector('.section-header, header.header, .shopify-section-header');
      const headerBottom = siteHeader ? siteHeader.getBoundingClientRect().bottom : 0;
      const content = this.shadowRoot.querySelector('.product-form-modal__content');
      if (content && headerBottom > 0) {
        content.style.top = `calc(${headerBottom}px + env(safe-area-inset-top, 0px))`;
        content.style.bottom = 'auto';
      } else if (content) {
        content.style.top = '';
        content.style.bottom = 'env(safe-area-inset-bottom, 0px)';
      }
    } catch (_) {}

    const productForm = document.querySelector('product-form');
    if (productForm) {
      productForm.style.position = 'relative';
      productForm.style.zIndex = '1';
    }
    this.modal.style.zIndex = '999999';
  }

  hideModal() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.modal.classList.remove('active');
    document.body.style.overflow = '';

    this.previousActiveElement?.focus();

    const productForm = document.querySelector('product-form');
    if (productForm) {
      productForm.style.zIndex = '';
    }

    const errorMessages = this.shadowRoot.querySelectorAll('.error-message.show');
    errorMessages.forEach(error => error.classList.remove('show'));
    
    // Clear saved questions when modal closes (optional - comment out if you want to preserve across modal opens)
    // this.savedQuestions = {};
  }

  updateFormType(tags) {
    this.productTags = tags;

    // Get current variant for question count
    const currentVariantId = document.querySelector('.product-variant-id')?.value;
    const productType = this.getProductType(currentVariantId);
    const form = this.shadowRoot.getElementById('CustomProductForm');
    if (!form) return;

    let formFields = '';
    if (
      productType === 'spell' ||
      productType === 'custom_spell' ||
      productType === 'cbtm_spell' ||
      productType === 'regular'
    ) {
      formFields = `
        <div class="form-row inline">
          <div class="name-field">
            <label for="customerName">Your Name</label>
            <input 
              type="text" 
              id="customerName" 
              name="customerName"
              required
              maxlength="100"
              autocomplete="name"
              placeholder="Enter your full name"
            >
            <div class="error-message" id="nameError">Please enter your name</div>
          </div>
          <div class="dob-field">
            <label for="customerDOB">Date of Birth</label>
            <input 
              type="text" 
              id="customerDOB" 
              name="customerDOB"
              placeholder="DD/MM/YYYY"
            >
            <div class="error-message" id="dobError">Invalid date format</div>
          </div>
        </div>
        <div class="form-row inline">
          <div class="name-field">
            <label for="targetName">Person of Interest</label>
            <input 
              type="text" 
              id="targetName" 
              name="targetName"
              maxlength="100"
              placeholder="Enter their full name"
            >
          </div>
          <div class="dob-field">
            <label for="targetDOB">Date of Birth</label>
            <input 
              type="text" 
              id="targetDOB" 
              name="targetDOB"
              placeholder="DD/MM/YYYY"
            >
          </div>
        </div>
        <div class="form-row">
          <label for="additionalDetails">Additional Details</label>
          <textarea 
            id="additionalDetails" 
            name="additionalDetails"
            maxlength="500"
            placeholder="Any specific details or requests..."
          ></textarea>
        </div>
      `;
    } else if (typeof productType === 'object' && productType.type === 'tarot') {
      formFields = `
        <div class="form-row inline">
          <div class="name-field">
            <label for="customerName">Your Name</label>
            <input 
              type="text" 
              id="customerName" 
              name="customerName"
              required
              maxlength="100"
              autocomplete="name"
              placeholder="Enter your full name"
            >
            <div class="error-message" id="nameError">Please enter your name</div>
          </div>
          <div class="dob-field">
            <label for="customerDOB">Date of Birth</label>
            <input 
              type="text" 
              id="customerDOB" 
              name="customerDOB"
              placeholder="DD/MM/YYYY"
            >
            <div class="error-message" id="dobError">Invalid date format</div>
          </div>
        </div>
        <div class="form-row inline">
          <div class="name-field">
            <label for="targetName">Person of Interest</label>
            <input 
              type="text" 
              id="targetName" 
              name="targetName"
              maxlength="100"
              placeholder="Enter their full name"
            >
          </div>
          <div class="dob-field">
            <label for="targetDOB">Date of Birth</label>
            <input 
              type="text" 
              id="targetDOB" 
              name="targetDOB"
              placeholder="DD/MM/YYYY"
            >
          </div>
        </div>
        ${this.createTarotFields(productType.questionCount, this.savedQuestions)}
      `;
    }

    formFields += `
      <button type="submit" class="submit-button">
        <span class="button-content">
          <span class="spinner" aria-hidden="true"></span>
          <span class="button-text">Update Cart</span>
        </span>
      </button>
    `;

    form.innerHTML = formFields;
    this.setupEventListeners();
  }

  editItem(properties) {
    Object.entries(properties).forEach(([key, value]) => {
      switch (key) {
        case 'Customer Name':
          this.shadowRoot.getElementById('customerName').value = value;
          break;
        case 'Date of Birth':
          this.shadowRoot.getElementById('customerDOB').value = value;
          break;
        case 'Person of Interest':
          this.shadowRoot.getElementById('targetName').value = value;
          break;
        case 'Person DOB':
          this.shadowRoot.getElementById('targetDOB').value = value;
          break;
        case 'Additional Details':
          const detailsInput = this.shadowRoot.getElementById('additionalDetails');
          if (detailsInput) detailsInput.value = value;
          break;
        default:
          if (key.startsWith('Question')) {
            const questionNum = key.replace('Question ', '');
            const questionInput = this.shadowRoot.getElementById(`question${questionNum}`);
            if (questionInput) questionInput.value = value;
          }
          break;
      }
    });
    this.showModal();
  }

  /**
   * Handle VIP stock updates after successful cart addition
   */
  handleVipStockUpdate() {
    // Find the VIP timer instance and decrement stock
    const vipTimerElements = document.querySelectorAll('.vip-timer-container');
    
    vipTimerElements.forEach(timerElement => {
      // Check if there's a VIP timer instance
      if (window.VipTimer && timerElement._vipTimerInstance) {
        const wasDecremented = timerElement._vipTimerInstance.decrementStock();
        if (wasDecremented) {
          console.log('VIP stock decremented successfully');
        }
      } else {
        // Fallback: dispatch event for VIP timer to listen to
        document.dispatchEvent(new CustomEvent('vip:decrement-stock'));
      }
    });
  }
}

if (!customElements.get('product-form-modal')) {
  customElements.define('product-form-modal', ProductFormModal);
}
